import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination, Autoplay } from 'swiper';
import HeroCarouselSlide from './HeroCarouselSlide.jsx';
import CarouselNavigation from './CarouselNavigation.jsx';
import VideoControls from './VideoControls.jsx';
import { isItemVideo, getSlideDuration } from './utils/carouselHelpers';
import { useSwiperHandlers } from './useSwiperHandlers';
import safePlay from './utils/safePlay';
import './HeroCarousel.scss';

SwiperCore.use([Pagination, Autoplay]);

const HeroCarousel = ({ items = [], displayDuration = 5000, context, hasCards = false }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isDesktopView, setIsDesktopView] = useState(typeof window !== 'undefined' ? window.innerWidth >= 769 : false);
    const [isMuted, setIsMuted] = useState(true);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [showControls, setShowControls] = useState(false);
    
    const videoRef = useRef(null);
    const swiperRef = useRef(null);
    const pausedIntentRef = useRef({});
    const advanceTimerRef = useRef(null);
    const controlsTimerRef = useRef(null);

    const hasMultipleItems = items.length > 1;

    useEffect(() => {
        const onResize = () => setIsDesktopView(window.innerWidth >= 769);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Swiper will handle autoplay; keep currentIndex in sync when autoplay or manual changes occur
    useEffect(() => {
        // If swiper is ready, toggle its autoplay controller. Also update params.autoplay so pagination/controls
        // reflect the current autoplay state when Swiper is re-initialized.
        const swiper = swiperRef.current;
        if (!swiper) return;

        // update autoplay param so Swiper has correct config if it needs to re-init
        if (swiper.params) swiper.params.autoplay = isPlaying && hasMultipleItems ? { delay: displayDuration, disableOnInteraction: false } : false;

        if (!isPlaying && swiper.autoplay) {
            swiper.autoplay.stop?.();
        }
        if (isPlaying && swiper.autoplay) {
            swiper.autoplay.start?.();
        }
    }, [isPlaying]);

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    const clearAdvanceTimer = () => {
        if (advanceTimerRef.current) {
            clearTimeout(advanceTimerRef.current);
            advanceTimerRef.current = null;
        }
    };

    const clearControlsTimer = () => {
        if (controlsTimerRef.current) {
            clearTimeout(controlsTimerRef.current);
            controlsTimerRef.current = null;
        }
    };

    const scheduleHideControls = () => {
        clearControlsTimer();
        // Only hide if video is playing
        if (isVideoPlaying) {
            controlsTimerRef.current = setTimeout(() => {
                setShowControls(false);
            }, 3000); // Hide after 3 seconds of inactivity
        }
    };

    const handleVideoInteraction = () => {
        setShowControls(true);
        scheduleHideControls();
    };

    const scheduleAdvanceIfPaused = (idx, ms) => {
        clearAdvanceTimer();
        if (!ms || ms <= 0) return;
        advanceTimerRef.current = setTimeout(() => {
            try {
                if (currentIndex !== idx) return;
                const item = items[idx];
                if (!isItemVideo(item)) return;
                const el = document.querySelector('.hero-carousel .swiper-slide-active video.carousel-media');
                if (el && el.paused) {
                    const s = swiperRef.current;
                    try { s?.slideNext?.(); } catch (e) { /* ignore */ }
                }
            } catch (e) { /* ignore */ }
        }, ms);
    };

    const handleVideoPlayPause = (e) => {
        // Show controls on interaction
        handleVideoInteraction();
        
        // target the active slide's video element (fallback to ref)
        const el = document.querySelector('.hero-carousel .swiper-slide-active video.carousel-media') || videoRef.current;
        if (!el) return;
        if (el.paused) {
            // optimistic UI update
            setIsVideoPlaying(true);
            // user is explicitly playing -> clear paused intent for this slide
            try { pausedIntentRef.current[currentIndex] = false; } catch (e) { /* ignore */ }
            safePlay(el).then(() => { setIsVideoPlaying(!el.paused); }).catch(() => {
                setIsVideoPlaying(!el.paused);
            });
        } else {
            try { el.pause(); } catch (e) { /* ignore */ }
            // user explicitly paused -> remember intent for this slide
            try { pausedIntentRef.current[currentIndex] = true; } catch (e) { /* ignore */ }
            setIsVideoPlaying(false);
            // Keep controls visible when paused
            setShowControls(true);
            clearControlsTimer();
            // schedule auto-advance for paused video
            const dur = getSlideDuration(items[currentIndex], displayDuration) || displayDuration;
            scheduleAdvanceIfPaused(currentIndex, dur);
        }
    };

    const toggleMute = () => {
        const newMuted = !isMuted;
        setIsMuted(newMuted);
        const el = document.querySelector('.hero-carousel .swiper-slide-active video.carousel-media');
        if (el) el.muted = newMuted;
    };

    const togglePlayPause = () => {
        // If the current slide is a video, control the video playback instead of toggling swiper autoplay
        const currentItem = items[currentIndex];
        if (isItemVideo(currentItem)) {
            // toggle video play/pause targeting active video element
            const el = document.querySelector('.hero-carousel .swiper-slide-active video.carousel-media') || videoRef.current;
            if (el) {
                if (el.paused) {
                    // user explicitly playing -> clear paused intent
                    try { pausedIntentRef.current[currentIndex] = false; } catch (e) { /* ignore */ }
                    setIsVideoPlaying(true);
                    safePlay(el).then(() => setIsVideoPlaying(!el.paused)).catch(() => {
                        setIsVideoPlaying(!el.paused);
                    });
                } else {
                    try { el.pause(); } catch (e) { /* ignore */ }
                    // user explicitly paused -> remember intent
                    try { pausedIntentRef.current[currentIndex] = true; } catch (e) { /* ignore */ }
                    setIsVideoPlaying(false);
                }
            }
            return;
        }

        // Non-video slides: toggle carousel autoplay
        setIsPlaying(!isPlaying);
    };

    // Toggle only the carousel autoplay (used by carousel play/pause button)
    const toggleAutoplay = () => {
        const s = swiperRef.current;
        const newState = !isPlaying;
        setIsPlaying(newState);
        if (s?.autoplay) {
            if (newState) s.autoplay.start?.(); else s.autoplay.stop?.();
        }
    };

    // Use extracted Swiper event handlers
    const { handleSlideChange, handleTransitionStart, handleTransitionEnd } = useSwiperHandlers({
        items,
        isMuted,
        isPlaying,
        pausedIntentRef,
        setCurrentIndex,
        clearAdvanceTimer
    });

    // Manage controls visibility based on video playing state
    useEffect(() => {
        if (isVideoPlaying) {
            // When video starts playing, hide controls after delay
            scheduleHideControls();
        } else {
            // When video is paused, clear the hide timer but don't automatically show controls
            clearControlsTimer();
        }
    }, [isVideoPlaying]);

    // Reset controls visibility when slide changes
    useEffect(() => {
        setShowControls(false);
        clearControlsTimer();
    }, [currentIndex]);

    // keep a minimal video activation effect — deeper logic lives in HeroCarouselSlide
    useEffect(() => {
        // pause any non-active videos
        const videos = document.querySelectorAll('.hero-carousel video.carousel-media');
        videos.forEach((v) => {
            if (v && typeof v.pause === 'function') {
                const parentSlide = v.closest('.swiper-slide');
                const idx = parentSlide ? Number(parentSlide.getAttribute('data-swiper-slide-index')) : null;
                if (idx !== currentIndex) {
                    try { v.pause(); } catch (e) { /* ignore */ }
                }
            }
        });
    }, [currentIndex]);

    return (
        <div className="hero-carousel">
            <Swiper
                className="carousel-container"
                allowTouchMove={hasMultipleItems}
                touchRatio={hasMultipleItems ? 1 : 0}
                dir="rtl"
                onSwiper={(s) => { swiperRef.current = s; }}
                onSlideChange={handleSlideChange}
                onTransitionStart={handleTransitionStart}
                onTransitionEnd={handleTransitionEnd}
                slidesPerView={1}
                navigation={false}
                pagination={hasMultipleItems ? { el: '.hero-carousel .carousel-navigation .swiper-pagination', clickable: true } : false}
                autoplay={isPlaying && hasMultipleItems ? { delay: displayDuration, disableOnInteraction: false } : false}
                loop={true}
            >
                {items.map((item, index) => {
                    const slideIsVideo = String(item?.mediaType?.value || '').toLowerCase() === 'video';
                    return (
                        <SwiperSlide
                            key={index}
                            className="carousel-slide"
                            data-swiper-autoplay={getSlideDuration(item, displayDuration) || undefined}
                            data-media-type={(item?.mediaType?.value || '').toLowerCase()}
                        >
                            {/* Render slide content via dedicated component */}
                            <HeroCarouselSlide
                                item={item}
                                index={index}
                                currentIndex={currentIndex}
                                isMuted={isMuted}
                                isPlaying={isPlaying}
                                pausedIntentRef={pausedIntentRef}
                                swiperRef={swiperRef}
                                onVideoPlayPause={handleVideoPlayPause}
                                onVideoClick={handleVideoInteraction}
                                onVideoStateChange={(idx, playing) => {
                                    if (idx === currentIndex) {
                                        setIsVideoPlaying(playing);
                                        if (playing) {
                                            // if user or autoplay started playing, cancel any paused-advance timer
                                            clearAdvanceTimer();
                                        } else {
                                            // if video is paused while active, schedule advance like an image
                                            const dur = getSlideDuration(items[idx], displayDuration) || displayDuration;
                                            scheduleAdvanceIfPaused(idx, dur);
                                        }
                                    }
                                }}
                            />
                            {/* video controls only for active video slides */}
                            {slideIsVideo && index === currentIndex && (
                                <VideoControls
                                    isVideoPlaying={isVideoPlaying}
                                    isMuted={isMuted}
                                    hasCards={hasCards}
                                    onPlayPause={handleVideoPlayPause}
                                    onToggleMute={toggleMute}
                                    showControls={showControls}
                                />
                            )}
                        </SwiperSlide>
                    );
                })}
            </Swiper>

            {hasMultipleItems && (
                <CarouselNavigation
                    isPlaying={isPlaying}
                    onToggleAutoplay={toggleAutoplay}
                    onPrev={() => swiperRef.current?.slidePrev()}
                    onNext={() => swiperRef.current?.slideNext()}
                    hasCards={hasCards}
                />
            )}
        </div>
    );
};

export default HeroCarousel;
